import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import CloseIcon from '@mui/icons-material/Close';
import axios from '../../api/axiosInstance';

const COLORS = ['#0f766e', '#2563eb', '#f59e0b', '#ef4444', '#7c3aed', '#0891b2', '#84cc16', '#ec4899', '#475569', '#14b8a6'];
const getEasyRedmineTicketUrl = (ticketId) => `https://toolbox.easyredmine.com/issues/${ticketId}`;

function formatHours(value) {
  return `${Number(value || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}h`;
}

function formatDate(value) {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatOpeningDays(createdAt, closedAt) {
  if (!createdAt || !closedAt) return '-';
  const start = new Date(createdAt);
  const end = new Date(closedAt);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return '-';
  const days = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  return `${days.toLocaleString('fr-FR', { maximumFractionDigits: 2 })} j`;
}

function formatDelta(value) {
  const number = Number(value || 0);
  return `${number > 0 ? '+' : ''}${formatHours(number)}`;
}

function getContractReference(contract = {}) {
  return contract.reference || contract.ref || contract.contractNumber || contract.name || 'Contrat';
}

function getContractName(contract = {}) {
  const reference = getContractReference(contract);
  const name = contract.name || contract.title || '';
  return name && name !== reference ? name : '';
}

function getContractLabel(contract = {}) {
  const reference = getContractReference(contract);
  const name = getContractName(contract);
  return name ? `${reference} - ${name}` : reference;
}

function ChartCard({ title, subtitle, children, minHeight = 320 }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, height: '100%' }}>
      <Stack spacing={1.5} sx={{ height: '100%' }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
          {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
        </Box>
        <Box sx={{ height: minHeight, minHeight, flex: '0 0 auto' }}>{children}</Box>
      </Stack>
    </Paper>
  );
}

function EmptyState({ label = 'Aucune donnée à afficher.' }) {
  return (
    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Stack>
  );
}



function ProfileTrackerChart({ data }) {
  if (!data?.length) return <EmptyState />;

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart data={data} margin={{ top: 16, right: 24, bottom: 8, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="profile" />
        <YAxis tickFormatter={formatHours} />
        <Tooltip formatter={(value) => formatHours(value)} />
        <Legend />
        <Bar dataKey="infogerance" name="Infogérance" stackId="tracker" fill="#0f766e" />
        <Bar dataKey="commande" name="Commande" stackId="tracker" fill="#2563eb" />
        <Bar dataKey="autre" name="Autre" stackId="tracker" fill="#94a3b8" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function RegularizationCard({ title, data }) {
  const profiles = [
    { key: 'admin', label: 'Heures admin' },
    { key: 'engineer', label: 'Heures ingé' },
    { key: 'pilotage', label: 'Pilotage' },
  ];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        height: '100%',
        borderColor: data?.status === 'to_regularize' ? 'warning.main' : 'divider',
        bgcolor: data?.status === 'to_regularize' ? 'rgba(245, 158, 11, 0.06)' : 'background.paper',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{title}</Typography>
          <Chip
            size="small"
            color={data?.status === 'to_regularize' ? 'warning' : 'success'}
            label={data?.status === 'to_regularize' ? `${formatHours(data?.toRegularize?.total)} à régulariser` : 'OK'}
          />
        </Stack>
        {profiles.map((profile) => {
          const delta = Number(data?.delta?.[profile.key] || 0);
          return (
            <Stack key={profile.key} spacing={0.25}>
              <Stack direction="row" justifyContent="space-between" spacing={1}>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>{profile.label}</Typography>
                <Typography variant="body2" color={delta > 0 ? 'warning.dark' : 'success.dark'} sx={{ fontWeight: 800 }}>
                  {delta > 0 ? `${formatHours(delta)} à régul` : `${formatHours(Math.abs(delta))} disponible`}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {formatHours(data?.used?.[profile.key])} utilisé / {formatHours(data?.included?.[profile.key])} prévu ({formatDelta(delta)})
              </Typography>
            </Stack>
          );
        })}
      </Stack>
    </Paper>
  );
}

function RegularizationSection({ regularization }) {
  if (!regularization) return null;
  const rows = regularization.byContract || [];

  return (
    <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Régularisations contrat</Typography>
          <Typography variant="body2" color="text.secondary">Comparaison entre les heures prévues dans les articles du contrat et les temps passés.</Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item size={{xs: 12, md: 6}}>
            <RegularizationCard title="Mois en cours" data={regularization.month} />
          </Grid>
          <Grid item size={{xs: 12, md: 6}}>
            <RegularizationCard title="Année en cours" data={regularization.year} />
          </Grid>
        </Grid>
        <TableContainer sx={{ maxHeight: 360 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 800 }}>Contrat</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Admin mois</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Ingé mois</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Admin année</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800 }}>Ingé année</TableCell>
                <TableCell sx={{ fontWeight: 800 }}>Source</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" sx={{ fontWeight: 800 }} noWrap>{getContractReference(row)}</Typography>
                      {getContractName(row) ? (
                        <Typography variant="caption" color="text.secondary" noWrap>{getContractName(row)}</Typography>
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell align="right">{formatDelta(row.month?.delta?.admin)}</TableCell>
                  <TableCell align="right">{formatDelta(row.month?.delta?.engineer)}</TableCell>
                  <TableCell align="right">{formatDelta(row.year?.delta?.admin)}</TableCell>
                  <TableCell align="right">{formatDelta(row.year?.delta?.engineer)}</TableCell>
                  <TableCell>{row.hasArticleHours ? `${row.articles?.length || 0} article(s)` : 'Champs contrat'}</TableCell>
                </TableRow>
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>Aucun contrat actif à analyser.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Paper>
  );
}

function PieBlock({ data }) {
  if (!data?.length) return <EmptyState />;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" outerRadius={95} label={({ name, value }) => `${name} ${formatHours(value)}`}>
          {data.map((_, index) => <Cell key={index} fill={COLORS[index % COLORS.length]} />)}
        </Pie>
        <Tooltip formatter={(value) => formatHours(value)} />
      </PieChart>
    </ResponsiveContainer>
  );
}

export default function ClientTimeAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('current');
  const [trackerFilter, setTrackerFilter] = useState('all');
  const [contractFilter, setContractFilter] = useState('all');
  const [selectedMonth, setSelectedMonth] = useState(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (trackerFilter !== 'all') params.set('trackerMode', trackerFilter);
    if (contractFilter !== 'all') params.set('contractId', contractFilter);
    const query = params.toString();
    axios.get(`/api/client/dashboard${query ? `?${query}` : ''}`)
      .then((response) => {
        if (!active) return;
        setData(response.data);
        setError('');
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || 'Impossible de charger les analyses client.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [trackerFilter, contractFilter]);

  const analyticsRoot = data?.analytics || {};
  const regularization = data?.regularization;
  const contracts = regularization?.byContract || [];
  const currentYearLabel = analyticsRoot.currentYearLabel || new Date().getFullYear();
  const previousYearLabel = analyticsRoot.previousYearLabel || currentYearLabel - 1;
  const analytics = viewMode === 'previous'
    ? analyticsRoot.previousYear || {}
    : analyticsRoot.currentYear || analyticsRoot || {};
  const comparison = analyticsRoot.comparison || {};
  const selectedYearLabel = viewMode === 'previous' ? previousYearLabel : currentYearLabel;
  const users = analytics.monthly?.users || [];
  const monthlyRows = useMemo(() => (analytics.monthly?.rows || []).map((row) => ({
    ...row,
    ...users.reduce((acc, user) => ({ ...acc, [user]: row.users?.[user] || 0 }), {}),
  })), [analytics.monthly?.rows, users]);

  const totals = useMemo(() => {
    if (viewMode === 'compare') {
      return {
        hours: comparison.totals?.current || 0,
        previousHours: comparison.totals?.previous || 0,
        interventions: comparison.totals?.currentInterventions || 0,
        previousInterventions: comparison.totals?.previousInterventions || 0,
        deltaHours: Number(comparison.totals?.current || 0) - Number(comparison.totals?.previous || 0),
        projects: analytics.projectMatrix?.rows?.length || 0,
      };
    }

    return {
      hours: monthlyRows.reduce((sum, row) => sum + Number(row.total || 0), 0),
      interventions: monthlyRows.reduce((sum, row) => sum + Number(row.interventions || 0), 0),
      projects: analytics.projectMatrix?.rows?.length || 0,
    };
  }, [monthlyRows, analytics.projectMatrix?.rows, comparison.totals, viewMode]);

  const radarData = (analytics.byCompetence || []).map((item) => ({ subject: item.name, value: item.value }));
  const selectedMonthDetails = selectedMonth?.details || [];
  const selectedMonthTotal = selectedMonthDetails.reduce((sum, entry) => sum + Number(entry.hours || 0), 0);

  const openMonthDetails = (rowOrEvent) => {
    const row = rowOrEvent?.payload || rowOrEvent?.activePayload?.[0]?.payload || rowOrEvent;
    if (!row?.month) return;
    const sourceRows = viewMode === 'compare' ? analyticsRoot.currentYear?.monthly?.rows || [] : analytics.monthly?.rows || [];
    const detailRow = sourceRows.find((item) => item.month === row.month) || row;
    setSelectedMonth({ ...detailRow, year: viewMode === 'previous' ? previousYearLabel : currentYearLabel });
  };

  if (loading) {
    return <Stack alignItems="center" sx={{ py: 8 }}><CircularProgress /></Stack>;
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
        <Box>
          <Typography variant="overline" sx={{ color: 'success.dark', fontWeight: 800 }}>{data?.organization?.name || 'Client'}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>Analyse des temps</Typography>
          <Typography color="text.secondary">Vue annuelle des temps passés, interventions, profils et compétences.</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: { xs: '100%', md: 'auto' } }}>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel id="client-time-tracker-label">Tracker</InputLabel>
          <Select
            labelId="client-time-tracker-label"
            value={trackerFilter}
            label="Tracker"
            onChange={(event) => { setTrackerFilter(event.target.value); setSelectedMonth(null); }}
          >
            <MenuItem value="all">Tous les trackers</MenuItem>
            <MenuItem value="infogerance">Infogérance</MenuItem>
            <MenuItem value="commande">Commande</MenuItem>
            <MenuItem value="autre">Autre</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 250 }}>
          <InputLabel id="client-time-contract-label">Contrat</InputLabel>
          <Select
            labelId="client-time-contract-label"
            value={contractFilter}
            label="Contrat"
            onChange={(event) => { setContractFilter(event.target.value); setSelectedMonth(null); }}
          >
            <MenuItem value="all">Tous les contrats</MenuItem>
            <MenuItem value="commands">Hors contrats / Commandes</MenuItem>
            {contracts.map((contract) => (
              <MenuItem key={contract._id} value={contract._id}>
                <Stack spacing={0.25}>
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{getContractReference(contract)}</Typography>
                  {getContractName(contract) ? (
                    <Typography variant="caption" color="text.secondary">{getContractName(contract)}</Typography>
                  ) : null}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="client-time-view-label">Période</InputLabel>
          <Select
            labelId="client-time-view-label"
            value={viewMode}
            label="Période"
            onChange={(event) => setViewMode(event.target.value)}
          >
            <MenuItem value="current">Année {currentYearLabel}</MenuItem>
            <MenuItem value="previous">Année {previousYearLabel}</MenuItem>
            <MenuItem value="compare">Comparatif {currentYearLabel} / {previousYearLabel}</MenuItem>
          </Select>
        </FormControl>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={viewMode === 'compare' ? `${formatHours(totals.hours)} en ${currentYearLabel}` : `${formatHours(totals.hours)} consommées`} />
        {viewMode === 'compare' && <Chip label={`${formatHours(totals.previousHours)} en ${previousYearLabel}`} />}
        {viewMode === 'compare' && <Chip color={totals.deltaHours >= 0 ? 'error' : 'success'} label={`${totals.deltaHours >= 0 ? '+' : ''}${formatHours(totals.deltaHours)} vs ${previousYearLabel}`} />}
        <Chip label={viewMode === 'compare' ? `${totals.interventions} intervention(s) ${currentYearLabel}` : `${totals.interventions} intervention(s)`} />
        {viewMode === 'compare' && <Chip label={`${totals.previousInterventions} intervention(s) ${previousYearLabel}`} />}
        <Chip label={`${users.length} intervenant(s)`} />
        <Chip label={`${totals.projects} projet(s)`} />
        {contractFilter !== 'all' && (
          <Chip
            color="primary"
            variant="outlined"
            label={contractFilter === 'commands'
              ? 'Hors contrats / Commandes'
              : getContractLabel(contracts.find((contract) => String(contract._id) === String(contractFilter)) || {})}
          />
        )}
      </Stack>

      <RegularizationSection regularization={regularization} />

      <ChartCard
        title={`Admin / Ingé par tracker - ${selectedYearLabel}`}
        subtitle="Helpdesk compté en infogérance, Commande isolé à part."
        minHeight={320}
      >
        <ProfileTrackerChart data={analytics.byProfileTracker || []} />
      </ChartCard>

      <ChartCard
        title={viewMode === 'compare' ? `Comparatif ${currentYearLabel} / ${previousYearLabel}` : `Temps passé et interventions - ${selectedYearLabel}`}
        subtitle={viewMode === 'compare' ? 'Comparaison mensuelle des heures et écart entre les deux années.' : 'Heures empilées par intervenant, nombre d\'interventions en pointillé.'}
        minHeight={390}
      >
        {viewMode === 'compare' ? (
          (comparison.monthly || []).length ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={comparison.monthly || []} margin={{ top: 16, right: 24, bottom: 8, left: 0 }} onClick={openMonthDetails}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" onClick={(event) => openMonthDetails({ month: event?.value })} style={{ cursor: 'pointer' }} />
                <YAxis yAxisId="hours" tickFormatter={formatHours} />
                <YAxis yAxisId="delta" orientation="right" tickFormatter={formatHours} />
                <Tooltip formatter={(value, name) => [name === 'delta' ? formatHours(value) : formatHours(value), name]} />
                <Legend />
                <Bar yAxisId="hours" dataKey="previous" name={`${previousYearLabel}`} fill="#94a3b8" cursor="pointer" onClick={openMonthDetails} />
                <Bar yAxisId="hours" dataKey="current" name={`${currentYearLabel}`} fill="#0f766e" cursor="pointer" onClick={openMonthDetails} />
                <Line yAxisId="delta" type="monotone" dataKey="delta" name="Écart" stroke="#ef4444" strokeWidth={2} dot activeDot={{ r: 6, onClick: (_, payload) => openMonthDetails(payload?.payload) }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <EmptyState />
        ) : monthlyRows.length ? (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyRows} margin={{ top: 16, right: 24, bottom: 8, left: 0 }} onClick={openMonthDetails}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" onClick={(event) => openMonthDetails({ month: event?.value })} style={{ cursor: 'pointer' }} />
              <YAxis yAxisId="hours" tickFormatter={formatHours} />
              <YAxis yAxisId="count" orientation="right" />
              <Tooltip formatter={(value, name) => [name === 'interventions' ? value : formatHours(value), name]} />
              <Legend />
              {users.map((user, index) => (
                <Bar key={user} yAxisId="hours" dataKey={user} stackId="time" fill={COLORS[index % COLORS.length]} cursor="pointer" onClick={openMonthDetails} />
              ))}
              <Line yAxisId="count" type="monotone" dataKey="interventions" stroke="#ef4444" strokeDasharray="5 5" strokeWidth={2} dot activeDot={{ r: 6, onClick: (_, payload) => openMonthDetails(payload?.payload) }} />
            </ComposedChart>
          </ResponsiveContainer>
        ) : <EmptyState />}
      </ChartCard>

      <Grid container spacing={2}>
        <Grid item size={{xs: 12, md: 4}}>
          <ChartCard title="Par profil" minHeight={290}><PieBlock data={analytics.byProfile || []} /></ChartCard>
        </Grid>
        <Grid item size={{xs: 12, md: 4}}>
          <ChartCard title="Par activité" minHeight={290}><PieBlock data={analytics.byActivity || []} /></ChartCard>
        </Grid>
        <Grid item size={{xs: 12, md: 4}}>
          <ChartCard title="Par facturation" minHeight={290}><PieBlock data={analytics.byBilling || []} /></ChartCard>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item size={{xs: 12, lg: 7}}>
          <ChartCard title="Temps par compétence" subtitle="Lecture rapide des domaines les plus consommés." minHeight={390}>
            {radarData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="72%">
                  <PolarGrid />
                  <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11 }} />
                  <PolarRadiusAxis tickFormatter={formatHours} />
                  <Radar dataKey="value" stroke="#0f766e" fill="#14b8a6" fillOpacity={0.35} />
                  <Tooltip formatter={(value) => formatHours(value)} />
                </RadarChart>
              </ResponsiveContainer>
            ) : <EmptyState />}
          </ChartCard>
        </Grid>
        <Grid item size={{xs: 12, lg: 5}}>
          <ChartCard title="Sujets récurrents" subtitle="Mots fréquents issus du titre et de la description des tickets." minHeight={390}>
            {analytics.wordCloud?.length ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.1, alignContent: 'center', minHeight: 300 }}>
                {analytics.wordCloud.slice(0, 55).map((word, index) => (
                  <Typography
                    key={`${word.text}-${index}`}
                    component="span"
                    sx={{
                      fontWeight: index < 12 ? 800 : 500,
                      fontSize: `${Math.min(34, 12 + Number(word.value || 1) * 2)}px`,
                      color: COLORS[index % COLORS.length],
                      lineHeight: 1,
                    }}
                  >
                    {word.text}
                  </Typography>
                ))}
              </Box>
            ) : <EmptyState />}
          </ChartCard>
        </Grid>
      </Grid>


      <Dialog
        fullScreen
        open={!!selectedMonth}
        onClose={() => setSelectedMonth(null)}
      >
        <Stack spacing={2} sx={{ p: 2.5, height: '100%', bgcolor: 'background.default' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Temps passés - {selectedMonth?.month} {selectedMonth?.year}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatHours(selectedMonthTotal)} au total · {selectedMonthDetails.length} ligne(s)
              </Typography>
            </Box>
            <IconButton onClick={() => setSelectedMonth(null)}>
              <CloseIcon />
            </IconButton>
          </Stack>

          <TableContainer sx={{ flex: 1, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, width: 92 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 280 }}>Ticket</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 145 }}>Tâche créée</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 145 }}>Tâche clôturée</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, width: 86 }}>Ouverture</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 90 }}>Tracker</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 120 }}>Activité</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 280 }}>Commentaire</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 210 }}>Compétence 1</TableCell>
                  <TableCell sx={{ fontWeight: 800, minWidth: 300 }}>Compétence 2</TableCell>
                  <TableCell sx={{ fontWeight: 800, width: 150 }}>Utilisateur</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, width: 70 }}>Heures</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedMonthDetails.map((entry) => (
                  <TableRow key={entry._id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(entry.spentOn)}</TableCell>
                    <TableCell sx={{ minWidth: 220, maxWidth: 260 }}>
                      {entry.ticketId ? (
                        <Link
                          href={getEasyRedmineTicketUrl(entry.ticketId)}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          variant="body2"
                          sx={{ fontWeight: 800 }}
                        >
                          #{entry.ticketId}
                        </Link>
                      ) : (
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>#-</Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.25,
                          fontSize: '0.68rem',
                        }}
                        title={entry.ticketSubject || ''}
                      >
                        {entry.ticketSubject || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.ticketCreatedAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(entry.ticketClosedAt)}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>{formatOpeningDays(entry.ticketCreatedAt, entry.ticketClosedAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.tracker || '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.activity || '-'}</TableCell>
                    <TableCell sx={{ minWidth: 280, maxWidth: 360 }}>
                      <Typography
                        variant="caption"
                        color={entry.comments ? 'text.primary' : 'text.secondary'}
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.25,
                          fontSize: '0.7rem',
                        }}
                        title={entry.comments || ''}
                      >
                        {entry.comments || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 210, maxWidth: 260 }}>
                      {entry.competence ? (
                        <Chip
                          size="small"
                          label={entry.competence}
                          variant="outlined"
                          color="primary"
                          sx={{
                            maxWidth: 230,
                            height: 18,
                            fontSize: '0.64rem',
                            '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                          }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 300, maxWidth: 420 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.18,
                          fontSize: '0.7rem',
                        }}
                        title={entry.competence2 || ''}
                      >
                        {entry.competence2 || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>{entry.user || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{formatHours(entry.hours)}</TableCell>
                  </TableRow>
                ))}
                {!selectedMonthDetails.length && (
                  <TableRow>
                    <TableCell colSpan={11} align="center" sx={{ color: 'text.secondary' }}>Aucun temps passé sur ce mois.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Dialog>

      <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Matrice projets / mois</Typography>
            <Typography variant="body2" color="text.secondary">{`Synthèse des heures par projet, sur ${selectedYearLabel}.`}</Typography>
          </Box>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 800, minWidth: 240 }}>Projet</TableCell>
                  {(analytics.projectMatrix?.months || []).map((month) => <TableCell key={month} align="right" sx={{ fontWeight: 800 }}>{month}</TableCell>)}
                  <TableCell align="right" sx={{ fontWeight: 800 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(analytics.projectMatrix?.rows || []).map((row) => (
                  <TableRow key={row.project} hover>
                    <TableCell sx={{ fontWeight: 700 }}>{row.project}</TableCell>
                    {(analytics.projectMatrix?.months || []).map((month) => <TableCell key={month} align="right">{row.months?.[month] ? formatHours(row.months[month]) : '-'}</TableCell>)}
                    <TableCell align="right" sx={{ fontWeight: 800 }}>{formatHours(row.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Paper>
    </Stack>
  );
}
